'use client';

import React, { useId, useRef } from 'react';
import classNames from 'classnames';

import { Caption, Text } from '@/design-system/components/Basic/Typography';
import { validateFile } from '@/utils/fileValidation';
import { UploadIcon } from '@/components/Icons/UploadIcon';

export interface DsFileInputProps {
  label?: string;
  accept?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  required?: boolean;
  error?: React.ReactNode | string;
  placeholder?: string;
  className?: string;
  wrapperClassName?: string;
}

export const FileInput: React.FC<DsFileInputProps> = ({
  label,
  accept,
  value,
  onChange,
  disabled = false,
  required,
  error,
  placeholder,
  className,
  wrapperClassName,
}) => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (file) {
      const validationResult = validateFile(file);
      if (!validationResult.isValid) {
        // The error message is handled in the top-level component; here, let's just reset the input.
        e.target.value = '';
        onChange?.(null);
        return;
      }
    }

    onChange?.(file);
  };

  const hasValue = !!value;
  const displayPlaceholder = placeholder ?? '';

  return (
    <div className={classNames('flex gap-3', wrapperClassName)}>
      {label && (
        <Text as="label" size="s" weight="semibold" htmlFor={inputId} className="text-ds-text-3">
          {label}
          {required && ' *'}
        </Text>
      )}

      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled}
      />

      <button
        type="button"
        onClick={handleClick}
        className={classNames(
          // Container layout: column with given padding
          'w-full flex items-start gap-ds-3 text-left',
          'p-5',
          'rounded-ds-5 border-2 border-dashed border-ds-border-soft-1',
          'bg-ds-bg-surface-soft-2 hover:bg-ds-bg-surface-soft-3 transition-colors',
          'cursor-pointer',
          disabled && 'opacity-60 cursor-not-allowed',
          className,
        )}
      >
        <div className="flex justify-between items-center gap-ds-4 w-full space-x-ds-4">
          <div
            className={classNames(
              'flex text-ds-text-3 truncate',
              hasValue && 'text-ds-text-1 font-semibold',
            )}
          >
            {hasValue ? value?.name : displayPlaceholder}
          </div>
          <div className="flex shrink-0 justify-center items-center w-10 h-10 p-2 gap-1 rounded-full bg-ds-bg-surface-soft-3">
            <UploadIcon />
          </div>
        </div>
      </button>

      {error && (
        <Caption as="p" className="text-ds-text-feedback-error">
          {error}
        </Caption>
      )}
    </div>
  );
};

export default FileInput;
